import httpClient from './httpClient';
import type { PaginatedResult } from '@/types';
import type {
  RegulatoryDocumentItem,
  RegulatoryDocumentFilters,
  CreateRegulatoryDocumentRequest,
  UpdateRegulatoryDocumentRequest,
  RecallItem,
  RecallDetail,
  RecallFilters,
  CreateRecallRequest,
  AffectedCustomer,
  VigilanceItem,
  VigilanceDetail,
  VigilanceFilters,
  CreateVigilanceRequest,
  UpdateVigilanceRequest,
  SubmitVigilanceRequest,
  CloseVigilanceRequest,
} from '@/types/regulatory';

export const regulatoryApi = {
  // ── Documents ───────────────────────────────────────────

  async getDocuments(
    mdFilters: RegulatoryDocumentFilters
  ): Promise<PaginatedResult<RegulatoryDocumentItem>> {
    const mdParams: Record<string, string | number> = {
      page: mdFilters.page,
      pageSize: mdFilters.pageSize,
    };
    if (mdFilters.search) mdParams.search = mdFilters.search;
    if (mdFilters.productId) mdParams.productId = mdFilters.productId;
    if (mdFilters.documentType) mdParams.documentType = mdFilters.documentType;
    if (mdFilters.status) mdParams.status = mdFilters.status;

    const mdResponse = await httpClient.get<PaginatedResult<RegulatoryDocumentItem>>(
      '/regulatory/documents',
      { params: mdParams }
    );
    return mdResponse.data;
  },

  async getExpiringDocuments(): Promise<RegulatoryDocumentItem[]> {
    const mdResponse = await httpClient.get<RegulatoryDocumentItem[]>(
      '/regulatory/documents/expiring'
    );
    return mdResponse.data;
  },

  // Сервер повертає лише id створеного документа
  async createDocument(
    mdRequest: CreateRegulatoryDocumentRequest
  ): Promise<string> {
    const mdResponse = await httpClient.post<string>(
      '/regulatory/documents',
      mdRequest
    );
    return mdResponse.data;
  },

  async updateDocument(
    mdRequest: UpdateRegulatoryDocumentRequest
  ): Promise<void> {
    await httpClient.put(`/regulatory/documents/${mdRequest.id}`, mdRequest);
  },

  // Soft delete
  async deleteDocument(mdId: string): Promise<void> {
    await httpClient.delete(`/regulatory/documents/${mdId}`);
  },

  // ── Recalls ─────────────────────────────────────────────

  async getRecalls(
    mdFilters: RecallFilters
  ): Promise<PaginatedResult<RecallItem>> {
    const mdParams: Record<string, string | number> = {
      page: mdFilters.page,
      pageSize: mdFilters.pageSize,
    };
    if (mdFilters.search) mdParams.search = mdFilters.search;
    if (mdFilters.status) mdParams.status = mdFilters.status;
    if (mdFilters.productId) mdParams.productId = mdFilters.productId;

    const mdResponse = await httpClient.get<PaginatedResult<RecallItem>>(
      '/regulatory/recalls',
      { params: mdParams }
    );
    return mdResponse.data;
  },

  async getRecallById(mdId: string): Promise<RecallDetail> {
    const mdResponse = await httpClient.get<RecallDetail>(
      `/regulatory/recalls/${mdId}`
    );
    return mdResponse.data;
  },

  // Сервер повертає лише id створеного recall
  async createRecall(mdRequest: CreateRecallRequest): Promise<string> {
    const mdResponse = await httpClient.post<string>(
      '/regulatory/recalls',
      mdRequest
    );
    return mdResponse.data;
  },

  async activateRecall(mdId: string): Promise<void> {
    await httpClient.post(`/regulatory/recalls/${mdId}/activate`);
  },

  async completeRecall(mdId: string): Promise<void> {
    await httpClient.post(`/regulatory/recalls/${mdId}/complete`);
  },

  async getAffectedCustomers(mdRecallId: string): Promise<AffectedCustomer[]> {
    const mdResponse = await httpClient.get<AffectedCustomer[]>(
      `/regulatory/recalls/${mdRecallId}/affected-customers`
    );
    return mdResponse.data;
  },

  // ── Vigilance ───────────────────────────────────────────

  async getVigilanceReports(
    mdFilters: VigilanceFilters
  ): Promise<PaginatedResult<VigilanceItem>> {
    const mdParams: Record<string, string | number> = {
      page: mdFilters.page,
      pageSize: mdFilters.pageSize,
    };
    if (mdFilters.search) mdParams.search = mdFilters.search;
    if (mdFilters.severity) mdParams.severity = mdFilters.severity;
    if (mdFilters.status) mdParams.status = mdFilters.status;
    if (mdFilters.productId) mdParams.productId = mdFilters.productId;

    const mdResponse = await httpClient.get<PaginatedResult<VigilanceItem>>(
      '/regulatory/vigilance',
      { params: mdParams }
    );
    return mdResponse.data;
  },

  async getVigilanceById(mdId: string): Promise<VigilanceDetail> {
    const mdResponse = await httpClient.get<VigilanceDetail>(
      `/regulatory/vigilance/${mdId}`
    );
    return mdResponse.data;
  },

  // Сервер повертає лише id створеного звіту
  async createVigilance(mdRequest: CreateVigilanceRequest): Promise<string> {
    const mdResponse = await httpClient.post<string>(
      '/regulatory/vigilance',
      mdRequest
    );
    return mdResponse.data;
  },

  async updateVigilance(mdRequest: UpdateVigilanceRequest): Promise<void> {
    await httpClient.put(`/regulatory/vigilance/${mdRequest.id}`, mdRequest);
  },

  // Тіло необов'язкове: номер звіту ДСМД, якщо вже відомий; порожній рядок не надсилаємо
  async submitVigilance(mdId: string, mdRequest: SubmitVigilanceRequest = {}): Promise<void> {
    const mdDsmdReportNumber = mdRequest.dsmdReportNumber?.trim();
    await httpClient.post(`/regulatory/vigilance/${mdId}/submit`, {
      id: mdId,
      ...(mdDsmdReportNumber ? { dsmdReportNumber: mdDsmdReportNumber } : {}),
    });
  },

  // Submitted → UnderInvestigation
  async startVigilanceInvestigation(mdId: string): Promise<void> {
    await httpClient.post(`/regulatory/vigilance/${mdId}/start-investigation`);
  },

  // Тіло команди містить id — сервер звіряє його з маршрутом
  async closeVigilance(mdId: string, mdRequest: CloseVigilanceRequest): Promise<void> {
    await httpClient.post(`/regulatory/vigilance/${mdId}/close`, { ...mdRequest, id: mdId });
  },
};
